<script setup lang="ts">
import { t } from "../i18n";
import { computed } from "vue";
import Icon from "./Icon.vue";
export type ChatNoticeVariant =
  "date" | "system" | "info" | "warning" | "encryption" | "unread" | "loading";
const props = withDefaults(
  defineProps<{
    variant?: ChatNoticeVariant;
    text?: string;
    icon?: string;
    actionLabel?: string;
    interactive?: boolean;
    count?: number;
    datetime?: string;
    /** An (i) before the notice opens what happened: who, when, from and to. */
    details?: boolean;
  }>(),
  { variant: "system" },
);
const emit = defineEmits<{ action: []; details: [] }>();
const symbol = computed(
  () =>
    props.icon ||
    (
      {
        encryption: "lock",
        info: "info",
        warning: "shield",
        loading: "",
      } as Record<string, string>
    )[props.variant],
);
const label = computed(() =>
  props.variant === "unread" && props.count !== undefined
    ? t("chat.unread", { count: props.count })
    : props.text,
);
</script>
<template>
  <div
    class="chat-notice-row"
    :class="'chat-notice-row--' + variant"
    :role="variant === 'unread' ? 'separator' : undefined"
    :aria-label="variant === 'unread' ? label : undefined"
  >
    <button
      v-if="details"
      type="button"
      class="chat-notice-info"
      :aria-label="t('event.info.open')"
      @click="emit('details')"
    >
      <Icon name="info" :size="15" />
    </button>
    <component
      :is="interactive || actionLabel ? 'button' : 'div'"
      :type="interactive || actionLabel ? 'button' : undefined"
      class="chat-notice"
      :class="'chat-notice--' + variant"
      :role="variant === 'loading' ? 'status' : undefined"
      @click="(interactive || actionLabel) && emit('action')"
    >
      <span
        v-if="variant === 'loading'"
        class="spinner chat-notice-spinner"
        aria-hidden="true"
      />
      <Icon v-else-if="symbol" :name="symbol" :size="13" />
      <time v-if="variant === 'date'" :datetime="datetime"
        >{{ label }}<slot
      /></time>
      <span v-else
        ><slot>{{ label }}</slot
        ><span v-if="actionLabel" class="chat-notice-action">{{
          " " + actionLabel
        }}</span></span
      >
    </component>
  </div>
</template>
